import * as THREE from 'three';

import { triangleRenderability } from './validation.js';
import { shadingMinDot, normalizeShading } from './shading.js';

// Two face normals are treated as "the same surface" (and so may share a
// render vertex + normal) when they're parallel within this tolerance — deliberately
// tight, matching REGION_NORMAL_EPSILON below: this is about recognizing a
// genuinely flat corner, not smoothing over a shallow crease.
const RENDER_NORMAL_EPSILON = 1e-6;

/**
 * Outward unit normal of a face directly from a flat position array —
 * display-only, so a degenerate face quietly yields the zero vector rather
 * than throwing (see `faceNormal` for the throwing, topology-editing-operation
 * version of this). "Degenerate" is decided by the shared
 * `triangleRenderability` rule, so the faces this hands a zero normal to are
 * exactly the faces `faceNormal` refuses to operate on and exactly the faces
 * subdivision refuses to construct — one rule, not three similar-looking ones.
 * `positions` is already the `Float32Array` this geometry renders from, so
 * `triangleRenderability`'s own `Math.fround` is an identity here.
 */
function flatFaceNormal(positions, indexOfId, face) {
  const corners = face.vertexIds.map((id) => {
    const i = indexOfId.get(id);
    return [positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]];
  });
  const { cross, crossLength, renderable } = triangleRenderability(corners);
  return renderable ? [cross[0] / crossLength, cross[1] / crossLength, cross[2] / crossLength] : [0, 0, 0];
}

/**
 * Twice the triangle's area, used to weight a smoothed normal.
 *
 * Area weighting rather than a plain mean because triangle areas across one
 * mesh vary by a lot — more than 4x on a UV sphere's pole caps — and an
 * unweighted average lets a sliver triangle pull the shared normal as hard as
 * the large face it sits beside. A degenerate face contributes zero, which is
 * consistent with it also contributing a zero normal.
 */
function faceWeight(positions, indexOfId, face) {
  const corners = face.vertexIds.map((id) => {
    const i = indexOfId.get(id);
    return [positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]];
  });
  const { crossLength, renderable } = triangleRenderability(corners);
  return renderable ? crossLength : 0;
}

function isZeroVector(v) {
  return v[0] === 0 && v[1] === 0 && v[2] === 0;
}

/** A degenerate face's zero normal is compatible with anything — it must not force a spurious hard-edge split at an otherwise-flat corner. */
function normalsShareASurface(a, b) {
  if (isZeroVector(a) || isZeroVector(b)) return true;
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2] > 1 - RENDER_NORMAL_EPSILON;
}

/** The same test generalised to an arbitrary angle threshold; `minDot` at angle 0 IS `1 - RENDER_NORMAL_EPSILON`. */
function normalsGroupTogether(a, b, minDot) {
  if (isZeroVector(a) || isZeroVector(b)) return true;
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2] > minDot;
}

/**
 * Group a vertex's incident faces into smoothing groups by walking SHARED
 * EDGES, not by comparing each face against whichever face happened to create
 * the group.
 *
 * Comparing against a representative is not auto-smoothing. It is
 * non-transitive: on a curved fan whose consecutive faces differ by 25° with a
 * 30° threshold, every shared edge is under the threshold and the whole fan
 * must smooth — but the third face is ~49° from the first, so a
 * representative test drops it into a second group. It is also order-sensitive,
 * since which face becomes the representative decides the partition.
 *
 * The correct rule is the one every DCC uses: two faces meeting at this vertex
 * belong to the same smoothing group when they share an EDGE through it and
 * that edge's dihedral angle is within the threshold. Smoothing groups are then
 * the connected components of that relation.
 *
 * Components are returned in first-incidence order so render-vertex indices
 * stay stable and independent of how the union-find happened to merge.
 */
function connectedGroups(incident, topology, faceNormals, minDot) {
  const parent = incident.map((_, index) => index);
  const find = (i) => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    while (parent[i] !== root) {
      const next = parent[i];
      parent[i] = root;
      i = next;
    }
    return root;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };

  // Two incident faces share an edge through this vertex exactly when they
  // share one of its neighbours within their own face cycles.
  const byNeighbour = new Map();
  incident.forEach(({ faceIndex, corner }, index) => {
    const ids = topology.faces[faceIndex].vertexIds;
    const previous = ids[(corner - 1 + ids.length) % ids.length];
    const next = ids[(corner + 1) % ids.length];
    for (const neighbour of [previous, next]) {
      let list = byNeighbour.get(neighbour);
      if (!list) {
        list = [];
        byNeighbour.set(neighbour, list);
      }
      list.push(index);
    }
  });

  for (const list of byNeighbour.values()) {
    // Two on a manifold edge; more only where the mesh is non-manifold, where
    // every pair across that edge is still considered on its own merits.
    for (let a = 0; a < list.length; a++) {
      for (let b = a + 1; b < list.length; b++) {
        const na = faceNormals[incident[list[a]].faceIndex];
        const nb = faceNormals[incident[list[b]].faceIndex];
        if (normalsGroupTogether(na, nb, minDot)) union(list[a], list[b]);
      }
    }
  }

  const groups = [];
  const groupOfRoot = new Map();
  incident.forEach(({ faceIndex, corner }, index) => {
    const root = find(index);
    let group = groupOfRoot.get(root);
    if (!group) {
      group = { normal: faceNormals[faceIndex], faceIndices: [], corners: [], coplanar: true };
      groupOfRoot.set(root, group);
      groups.push(group);
    }
    if (!normalsShareASurface(group.normal, faceNormals[faceIndex])) group.coplanar = false;
    group.faceIndices.push(faceIndex);
    group.corners.push(`${faceIndex}:${corner}`);
  });
  return groups;
}

/**
 * The pre-Milestone-D grouping, kept verbatim for `flat`.
 *
 * Deliberately NOT replaced by `connectedGroups` at a zero threshold. The two
 * disagree wherever a vertex has coplanar faces that do not share an edge
 * through it — two coplanar surfaces meeting only at a point, or separated
 * around the fan by a perpendicular face. Legacy merges those into one render
 * vertex; edge-walking keeps them apart. Both are defensible, but `flat` must
 * reproduce previous output byte for byte, so `flat` keeps the behaviour it
 * has always had.
 */
function legacyGroups(incident, faceNormals) {
  const groups = [];
  for (const { faceIndex, corner } of incident) {
    const normal = faceNormals[faceIndex];
    let group = groups.find((g) => normalsShareASurface(g.normal, normal));
    if (!group) {
      group = { normal, faceIndices: [], corners: [], coplanar: true };
      groups.push(group);
    }
    group.faceIndices.push(faceIndex);
    group.corners.push(`${faceIndex}:${corner}`);
  }
  return groups;
}

/**
 * The normal a group of incident faces renders with.
 *
 * A group whose members are all mutually coplanar within
 * `RENDER_NORMAL_EPSILON` renders its representative's normal *exactly*. That
 * is not an optimisation: averaging a set of near-identical unit vectors is not
 * bit-stable, and `flat` mode must reproduce pre-Milestone-D output byte for
 * byte. It also means a genuinely flat face stays exactly flat under `smooth`,
 * which is the right answer anyway.
 *
 * Otherwise the group is a real blend and renders the area-weighted mean.
 * Normals that cancel out entirely (a degenerate fold) fall back to the
 * representative rather than emitting a zero normal the renderer cannot light.
 */
function groupNormal(group, faceNormals, faceWeights) {
  if (group.coplanar) return group.normal;
  let x = 0, y = 0, z = 0;
  for (const faceIndex of group.faceIndices) {
    const n = faceNormals[faceIndex];
    const w = faceWeights[faceIndex];
    x += n[0] * w;
    y += n[1] * w;
    z += n[2] * w;
  }
  const length = Math.sqrt(x * x + y * y + z * z);
  if (!(length > 0)) return group.normal;
  return [x / length, y / length, z / length];
}

/**
 * Rebuild a THREE.BufferGeometry from canonical topology. Always a fresh
 * geometry, never mutated in place.
 *
 * Render vertices are *not* simply one-per-topology-vertex: a topology
 * vertex is only ever shared by faces whose flat normals agree (within
 * `RENDER_NORMAL_EPSILON`) — genuinely coplanar faces meeting at that corner
 * (a box's two half-quad triangles, a region cap's interior diagonal) share
 * one render vertex and normal, so the surface reads as continuously flat
 * with no diagonal seam. Faces whose normals disagree at that corner — a
 * real hard edge, e.g. an extruded cap meeting its own side walls — each get
 * their own render-vertex copy carrying their own face's exact normal,
 * instead of being blended into `computeVertexNormals`'s shared-vertex
 * average (the "inflated/melted" look a naive indexed rebuild produces).
 * This is render-only splitting: topology vertex/face ids and counts are
 * completely unaffected, only the derived BufferGeometry has more vertices
 * than the topology where a hard edge requires it.
 *
 * `shading` (Phase 2, Milestone D) selects the angle at which that split
 * happens. Omitted or `flat` reproduces the behaviour described above exactly,
 * byte for byte, and is the default for every mesh that does not ask for
 * anything else. `smooth` shares across every face meeting at a topology
 * vertex; `auto` splits above its `angleDeg`. Shading never touches topology —
 * vertex and face ids and counts are identical in every mode.
 *
 * When every topology vertex already belongs to a single normal group —
 * true of any topology captured from a THREE.js primitive, which already
 * duplicates vertices across its own hard edges/UV seams (see the module
 * doc comment) — this reduces to exactly one render vertex per topology
 * vertex, in the same order, so a round trip through capture → build is
 * byte-identical to the source geometry.
 */
export function buildGeometryFromTopology(topology, shading) {
  const minDot = shadingMinDot(shading);
  const isFlat = normalizeShading(shading).mode === 'flat';
  const indexOfId = new Map(topology.vertices.map((vertex, index) => [vertex.id, index]));
  const positions = new Float32Array(topology.vertices.length * 3);
  topology.vertices.forEach((vertex, index) => {
    positions[index * 3] = vertex.position[0];
    positions[index * 3 + 1] = vertex.position[1];
    positions[index * 3 + 2] = vertex.position[2];
  });

  const faceNormals = topology.faces.map((face) => flatFaceNormal(positions, indexOfId, face));
  const faceWeights = topology.faces.map((face) => faceWeight(positions, indexOfId, face));

  // Every (faceIndex, corner) a given topology vertex participates in, in
  // face order — the basis for grouping that corner's incident faces by
  // shared-surface normal below.
  const incidentByVertexId = new Map();
  topology.faces.forEach((face, faceIndex) => {
    face.vertexIds.forEach((vertexId, corner) => {
      let list = incidentByVertexId.get(vertexId);
      if (!list) {
        list = [];
        incidentByVertexId.set(vertexId, list);
      }
      list.push({ faceIndex, corner });
    });
  });

  const renderPositions = [];
  const renderNormals = [];
  const renderIndexByCorner = new Map(); // `${faceIndex}:${corner}` -> render vertex index

  topology.vertices.forEach((vertex, vertexIndex) => {
    const incident = incidentByVertexId.get(vertex.id) ?? [];
    // Groups in first-incidence order, so a vertex with a single group — the
    // common case — contributes exactly one render vertex, keeping that case's
    // render-vertex index identical to its plain topology-vertex index.
    //
    // Two passes, because a smoothed group's normal is only known once every
    // member has joined it. The emit pass walks `groups` in that same order, so
    // render-vertex indices are exactly what the previous version produced.
    const groups = isFlat
      ? legacyGroups(incident, faceNormals)
      : connectedGroups(incident, topology, faceNormals, minDot);

    for (const group of groups) {
      const renderIndex = renderPositions.length / 3;
      renderPositions.push(positions[vertexIndex * 3], positions[vertexIndex * 3 + 1], positions[vertexIndex * 3 + 2]);
      const normal = groupNormal(group, faceNormals, faceWeights);
      renderNormals.push(normal[0], normal[1], normal[2]);
      for (const key of group.corners) renderIndexByCorner.set(key, renderIndex);
    }
  });

  const indices = [];
  topology.faces.forEach((face, faceIndex) => {
    face.vertexIds.forEach((vertexId, corner) => {
      const renderIndex = renderIndexByCorner.get(`${faceIndex}:${corner}`);
      if (renderIndex === undefined) throw new Error(`face '${face.id}' references unknown vertex '${vertexId}'`);
      indices.push(renderIndex);
    });
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(renderPositions), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(renderNormals), 3));
  geometry.setIndex(indices);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
